import { contentCatalog } from "../../content/catalog"
import { floraTextureId } from "../flora"
import { PlantedTileSpec } from "./forest_trees"

/**
 * The ember camp's bushes: the procedural grove bushes (packages/tools/src/trees, BUSHES), which
 * replace the LPC bush1 / bush2 / smallBush of flora.ts BUSH_TILES -- those bake in a ground shadow.
 *
 * They are SCENERY composites in authored-terrain (no entity tile, no item: a camp's undergrowth is
 * deliberately not breakable, see flora.ts UNDERGROWTH_TILES), found by composite id because a saved
 * Lab tile lands in whatever cell was free.
 *
 * One pick is ONE draw from this list, as `randomOneOf(BUSH_TILES)` was, and the list is laid out so
 * each quarter of that draw lands on the SAME size as BUSH_TILES = [bush2, smallBush, smallBush, bush1]
 * did: a 2x2 light bush, two 1x1 shrubs, a 2x2 deep bush. A room therefore grows exactly as before --
 * same cells, same footprints, same tree-overlap rejections -- and only the art changes.
 */
export const EMBER_BUSH_IDS = [
    "grove-bush-a", "grove-bush-b", // bush2: light, 2x2
    "grove-shrub-b", "grove-shrub-a", // smallBush
    "grove-shrub-c", "grove-shrub-a", // smallBush
    "grove-bush-c", "grove-bush-d", // bush1: deep, 2x2
]

const EMBER_BUSH_SET = new Set(EMBER_BUSH_IDS)

/** True for one of the camp's bush composites, by its composite id -- e.g. so the client rings it with
 * the same grass fringe it gives a tree's roots. */
export const isEmberBushComposite = (compositeId: string): boolean => EMBER_BUSH_SET.has(compositeId)

const property = (tile: any, name: string) => tile?.properties?.find((p: any) => p.name === name)?.value

let parentsFor: unknown
let parents: Map<string, PlantedTileSpec>

/** Where composite `id` is planted -- its parent cell -- read from the active catalog. */
export const compositeParent = (id: string): PlantedTileSpec | undefined => {
    if (parentsFor !== contentCatalog.mapTilesets) {
        parents = new Map()
        for (const [tilesetId, sheet] of Object.entries(contentCatalog.mapTilesets)) {
            for (const tile of (sheet as any).tiles || []) {
                const composite = property(tile, "compositeTileIdParent")
                if (composite && !parents.has(composite)) parents.set(composite, { tilesetId, tilePos: tile.id })
            }
        }
        parentsFor = contentCatalog.mapTilesets
    }
    return parents.get(id)
}

/** The bush pool, resolved: one entry per EMBER_BUSH_IDS entry, weights and all. */
export const emberBushes = (): PlantedTileSpec[] =>
    EMBER_BUSH_IDS.map(id => {
        const planted = compositeParent(id)
        // Loud rather than a bare camp: the art ships with the code that plants it.
        if (!planted) throw new Error(`Ember bush ${id} is not in the content catalog`)
        return planted
    })

/** True when (tilesetId, tilePos) plants one of the camp's bushes. */
export const isEmberBush = (tilesetId: string, tilePos: number): boolean =>
    emberBushes().some(bush => bush.tilesetId === tilesetId && bush.tilePos === tilePos)

/** The texture ids the client draws the camp's bushes under (see flora.ts floraTextureId). */
export const emberBushTextureIds = (): string[] =>
    [...new Set(emberBushes().map(bush => floraTextureId(bush.tilesetId, bush.tilePos)))]
